import { HELP_REQUEST_TYPE } from '../components/generative-ui/helpRequest';
import { findGenerativeUiDescriptor } from '../components/generative-ui/registry';
import { getVariantUrl, mediaTypeFromFileName } from '../upload';
import { parseCtaLink } from './ctaLink';
import type { ChatMessage } from '../client/types';
import type { ChatImageAttachment, DisplayMessage } from './types';

export type ReportError = (error: unknown, context: Record<string, unknown>) => void;

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Reconstructs the render-ready attachments of a persisted `image-upload` message.
 *
 * The service stores `data.value` verbatim (it is opaque to the backend) and strips the
 * base64 `images` array, so on read each entry is `{ key, name, size? }` and there is no
 * inline image data — the preview URL is rebuilt from the `key`. Non-object or key-less
 * entries are skipped rather than failing the whole message; a message that yields no
 * usable attachments is reported and rendered as unparseable by the caller.
 */
function parseImageUploadAttachments(data: unknown): {
    attachments: ChatImageAttachment[];
    caption?: string;
} | null {
    if (!isRecord(data) || !Array.isArray(data['value'])) {
        return null;
    }

    const attachments: ChatImageAttachment[] = [];
    for (const entry of data['value']) {
        if (!isRecord(entry) || typeof entry['key'] !== 'string') continue;
        const key = entry['key'];
        const fileName = typeof entry['name'] === 'string' ? entry['name'] : key;
        attachments.push({
            uploadId: key,
            previewUrl: getVariantUrl(key, 'preview'),
            fileName,
            mediaType: mediaTypeFromFileName(fileName),
            fileSize: typeof entry['size'] === 'number' ? entry['size'] : undefined,
        });
    }

    if (attachments.length === 0) return null;

    const caption =
        typeof data['message'] === 'string' && data['message'] !== '' ? data['message'] : undefined;

    return { attachments, caption };
}

/**
 * Turns a raw `ChatMessage` into its settled `DisplayMessage` form.
 *
 * Only user-role content is ever JSON-parsed: assistant content is always plain prose from the
 * model and must never be run through JSON.parse, so that check runs first and short-circuits
 * everything below it.
 */
export function normalizeMessage(
    message: ChatMessage,
    key: string,
    reportError: ReportError
): DisplayMessage {
    const { role, content } = message;

    if (role !== 'user') {
        // Reading `component` here does not weaken the rule above: it arrives already
        // structured on its own field, so nothing in the prose is being parsed.
        return { kind: 'text', content, key, role, ctaLink: parseCtaLink(message.component) };
    }

    if (content.trim() === '') {
        return { kind: 'text', content, key, role };
    }

    let parsed: unknown;
    try {
        parsed = JSON.parse(content);
    } catch {
        // Plain text is the common case here, not an error — never report this.
        return { kind: 'text', content, key, role };
    }

    if (parsed === null || Array.isArray(parsed)) {
        return { kind: 'unrecognizedJson', key, role };
    }

    if (!isRecord(parsed)) {
        // A JSON primitive (string/number/boolean) parsed successfully but has no `.type` to
        // dispatch on — render the original raw string, not the parsed value.
        return { kind: 'text', content, key, role };
    }

    const type = parsed['type'];
    if (typeof type !== 'string' || type === '') {
        return { kind: 'unrecognizedJson', key, role };
    }

    if (type === HELP_REQUEST_TYPE) {
        return { kind: 'helpRequest', key, role };
    }

    if (type === 'image-upload') {
        const parsedUpload = parseImageUploadAttachments(parsed['data']);
        if (parsedUpload === null) {
            reportError(new Error('image-upload message had no usable attachments'), {
                messageId: message.id,
                componentType: type,
            });
            return { kind: 'unparseable', key, role };
        }
        return {
            kind: 'imageUpload',
            attachments: parsedUpload.attachments,
            caption: parsedUpload.caption,
            key,
            role,
        };
    }

    const descriptor = findGenerativeUiDescriptor(type);
    if (!descriptor) {
        // Deliberately silent: an as-yet-unregistered but real type lands here, and
        // reporting would flood error tracking with false positives.
        return { kind: 'unrecognizedJson', key, role };
    }

    const payload = descriptor.parseDisplayPayload(parsed['data']);
    if (payload === null) {
        reportError(new Error('Generative-UI reply failed to parse'), {
            messageId: message.id,
            componentType: type,
        });
        return { kind: 'unparseable', key, role };
    }

    return { kind: 'generativeUi', key, role, ...payload };
}
